"use client";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useSpring,
} from "motion/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatedTabPanel } from "@/components/motion/animated-tab-panel";
import { cn } from "@/lib/utils";
import type { SpringConfig } from "../chart-config-context";

/** How long to wait for a scroll to start before assuming none was triggered. */
const SCROLL_START_GRACE_MS = 60;
/** Quiet period after the last scroll frame that counts as "scrolling stopped". */
const SCROLL_QUIET_MS = 100;
/** Upper bound on the wait — a never-quiet scroller must not hide the panel forever. */
const SCROLL_SETTLE_CAP_MS = 1200;

const VIEWPORT_EDGE_GAP = 8;
const VIEWPORT_OFFSET = 12;
const CONTAINER_OFFSET = 16;
const ABOVE_POINT_GAP = 12;

export type FloatingTooltipPlacement = "beside" | "above";

export type TooltipPositionValue = number | ReturnType<typeof useSpring>;

export type FloatingTooltipAnchor =
  | {
      /** Panel is `position: fixed` and portals to `document.body`. */
      mode: "viewport";
      /** DOM element to hang off, or a point already in viewport coordinates. */
      target: HTMLElement | SVGElement | { x: number; y: number } | null;
      /** Gap between the anchor and the panel. Default: 12. */
      offset?: number;
    }
  | {
      /** Panel is `position: absolute` and portals into `container`. */
      mode: "container";
      container: HTMLElement;
      /** Point in the container's pixel coordinate space. */
      x: number;
      y: number;
      /** Container box used for flipping and clamping. */
      width: number;
      height: number;
      /** Gap between the point and the panel. Default: 16. */
      offset?: number;
      placement?: FloatingTooltipPlacement;
    };

export interface FloatingTooltipProps {
  visible: boolean;
  anchor: FloatingTooltipAnchor;
  children: React.ReactNode;
  /** Identity of the rendered content — set it to morph between anchors. */
  contentKey?: string;
  /** Classes for the positioned wrapper. */
  className?: string;
  /** Inline styles for the positioned wrapper. */
  style?: React.CSSProperties;
  /** Classes for the visible panel. */
  panelClassName?: string;
  /** Inline styles for the visible panel. */
  panelStyle?: React.CSSProperties;
  /** Spring the panel to its position instead of placing it directly. */
  animatePosition?: boolean;
  springConfig?: SpringConfig;
  /** Fade / scale the panel on show. Default: true */
  entrance?: boolean;
  /** Override the computed left (bypasses placement). Only with `animatePosition`. */
  left?: TooltipPositionValue;
  /** Override the computed top (bypasses placement). Only with `animatePosition`. */
  top?: TooltipPositionValue;
  /** Force the flip direction instead of deriving it from the measured width. */
  flipped?: boolean;
}

interface AnchorPoint {
  x: number;
  y: number;
  height: number;
}

interface Placement {
  left: number;
  top: number;
  flipped: boolean;
  below?: boolean;
}

function resolveTargetPoint(
  target: Extract<FloatingTooltipAnchor, { mode: "viewport" }>["target"]
): AnchorPoint | null {
  if (!target) {
    return null;
  }
  if ("getBoundingClientRect" in target) {
    const rect = target.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top, height: rect.height };
  }
  return { x: target.x, y: target.y, height: 0 };
}

function samePoint(a: AnchorPoint | null, b: AnchorPoint | null): boolean {
  if (a === b) {
    return true;
  }
  return Boolean(
    a && b && a.x === b.x && a.y === b.y && a.height === b.height
  );
}

function placeAboveAnchor(
  point: AnchorPoint,
  w: number,
  h: number,
  offset: number,
  vw: number,
  vh: number
): Placement {
  let left = point.x - w / 2;
  let top = point.y - h - offset;
  if (top < VIEWPORT_EDGE_GAP) {
    top = point.y + point.height + offset;
  }
  if (left < VIEWPORT_EDGE_GAP) {
    left = VIEWPORT_EDGE_GAP;
  }
  if (left + w > vw - VIEWPORT_EDGE_GAP) {
    left = vw - w - VIEWPORT_EDGE_GAP;
  }
  if (top + h > vh - VIEWPORT_EDGE_GAP) {
    top = vh - h - VIEWPORT_EDGE_GAP;
  }
  return { left, top, flipped: false };
}

function placeBesidePoint(
  point: AnchorPoint,
  w: number,
  h: number,
  offset: number,
  cw: number,
  ch: number
): Placement {
  const flipped = point.x + w + offset > cw;
  return {
    left: flipped ? point.x - offset - w : point.x + offset,
    top: Math.max(offset, Math.min(point.y - h / 2, ch - h - offset)),
    flipped,
  };
}

function placeAbovePoint(
  point: AnchorPoint,
  w: number,
  h: number,
  cw: number,
  ch: number
): Placement {
  const left = Math.max(0, Math.min(point.x - w / 2, cw - w));
  const above = point.y - h - ABOVE_POINT_GAP;
  const below = point.y + ABOVE_POINT_GAP;
  const fitsAbove = above >= 0;
  const fitsBelow = below + h <= ch;
  const useBelow = !fitsAbove && (fitsBelow || ch - point.y > point.y);
  return {
    left,
    top: useBelow ? below : Math.max(0, above),
    flipped: false,
    below: useBelow,
  };
}

export function FloatingTooltip({
  visible,
  anchor,
  children,
  contentKey,
  className,
  style,
  panelClassName,
  panelStyle,
  animatePosition = false,
  springConfig,
  entrance = true,
  left: leftOverride,
  top: topOverride,
  flipped: flippedOverride,
}: FloatingTooltipProps) {
  const isViewport = anchor.mode === "viewport";
  const offset =
    anchor.offset ?? (isViewport ? VIEWPORT_OFFSET : CONTAINER_OFFSET);
  const target = anchor.mode === "viewport" ? anchor.target : null;
  const containerEl = anchor.mode === "container" ? anchor.container : null;
  const anchorX = anchor.mode === "container" ? anchor.x : 0;
  const anchorY = anchor.mode === "container" ? anchor.y : 0;
  const frameWidth = anchor.mode === "container" ? anchor.width : 0;
  const frameHeight = anchor.mode === "container" ? anchor.height : 0;
  const placeAbove =
    anchor.mode === "container" && anchor.placement === "above";

  const reduceMotion = useReducedMotion() ?? false;
  const [mounted, setMounted] = useState(false);
  const [viewportPoint, setViewportPoint] = useState<AnchorPoint | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [scrollSettled, setScrollSettled] = useState(true);
  const [scrollDismissed, setScrollDismissed] = useState(false);
  const [flipKey, setFlipKey] = useState(0);
  // Every show starts a new open cycle. `measured` is stamped with the cycle it
  // belongs to, so a reopen invalidates it without a reset that could race the
  // measuring effect in the same commit.
  const [openId, setOpenId] = useState(0);
  const [measuredId, setMeasuredId] = useState(-1);
  const measured = measuredId === openId;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const wasVisibleRef = useRef(false);
  const wasTargetRef = useRef<Element | null>(null);
  const placedRef = useRef(false);
  const prevFlipRef = useRef<boolean | null>(null);

  const targetEl =
    target && "getBoundingClientRect" in target ? (target as Element) : null;

  const retained = useRef<{ body: React.ReactNode; key?: string }>({
    body: children,
    key: contentKey,
  });
  if (visible) {
    retained.current = { body: children, key: contentKey };
  }
  const body = visible ? children : retained.current.body;
  const swapKey = visible ? contentKey : retained.current.key;

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    setScrollDismissed(false);
  }, [targetEl]);

  // Opening a tooltip often triggers a smooth scroll that brings the anchor into
  // view. Placing the panel before that scroll has finished makes it chase the
  // anchor across the whole animation, so placement waits for the scroll to stop.
  useLayoutEffect(() => {
    const reopened = visible && !wasVisibleRef.current;
    if (reopened) {
      setOpenId((id) => id + 1);
    }
    if (visible && targetEl && (reopened || wasTargetRef.current !== targetEl)) {
      setScrollSettled(false);
    }
    wasVisibleRef.current = visible;
    wasTargetRef.current = targetEl;
  }, [visible, targetEl]);

  useEffect(() => {
    if (!(visible && targetEl) || scrollSettled) {
      return;
    }

    let quiet: ReturnType<typeof setTimeout> | undefined;
    let done = false;

    const settle = () => {
      if (done) {
        return;
      }
      done = true;
      setScrollSettled(true);
    };

    const onScroll = () => {
      if (done) {
        return;
      }
      clearTimeout(quiet);
      quiet = setTimeout(settle, SCROLL_QUIET_MS);
    };

    quiet = setTimeout(settle, SCROLL_START_GRACE_MS);
    const cap = setTimeout(settle, SCROLL_SETTLE_CAP_MS);

    window.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("scrollend", settle, { capture: true });
    return () => {
      clearTimeout(quiet);
      clearTimeout(cap);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("scrollend", settle, true);
    };
  }, [visible, targetEl, scrollSettled]);

  useEffect(() => {
    if (!(visible && targetEl && scrollSettled)) {
      return;
    }

    const update = () => {
      const rect = targetEl.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const outOfView =
        rect.bottom < 0 || rect.top > vh || rect.right < 0 || rect.left > vw;
      if (outOfView) {
        setScrollDismissed(true);
      } else {
        const next = {
          x: rect.left + rect.width / 2,
          y: rect.top,
          height: rect.height,
        };
        setViewportPoint((prev) => (samePoint(prev, next) ? prev : next));
      }
    };

    window.addEventListener("scroll", update, { passive: true, capture: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [visible, targetEl, scrollSettled]);

  useLayoutEffect(() => {
    if (!(visible && isViewport)) {
      return;
    }
    if (targetEl && !scrollSettled) {
      return;
    }
    const next = resolveTargetPoint(target);
    setViewportPoint((prev) => (samePoint(prev, next) ? prev : next));
  }, [visible, isViewport, target, targetEl, scrollSettled]);

  const point: AnchorPoint | null = isViewport
    ? viewportPoint
    : { x: anchorX, y: anchorY, height: 0 };

  const w = size?.w ?? 0;
  const h = size?.h ?? 0;
  const gateOpen = !(isViewport && targetEl && !scrollSettled);
  const hasPanel = visible && point !== null && !scrollDismissed;

  const placement = point
    ? isViewport
      ? placeAboveAnchor(
          point,
          w,
          h,
          offset,
          typeof window === "undefined" ? 0 : window.innerWidth,
          typeof window === "undefined" ? 0 : window.innerHeight
        )
      : placeAbove
        ? placeAbovePoint(point, w, h, frameWidth, frameHeight)
        : placeBesidePoint(point, w, h, offset, frameWidth, frameHeight)
    : null;

  const readSize = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    const nextW = el.offsetWidth;
    const nextH = el.offsetHeight;
    setSize((prev) =>
      prev && prev.w === nextW && prev.h === nextH ? prev : { w: nextW, h: nextH }
    );
  }, []);

  // The panel is painted only once it has been measured: with an unknown size it
  // would land on the anchor and then correct itself in full view.
  useLayoutEffect(() => {
    if (!(hasPanel && gateOpen) || !wrapperRef.current) {
      return;
    }
    readSize();
    setMeasuredId(openId);
  });

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!hasPanel || !el) {
      return;
    }
    const observer = new ResizeObserver(readSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasPanel, readSize]);

  const springLeft = useSpring(0, springConfig);
  const springTop = useSpring(0, springConfig);

  // The first placement jumps: the panel is still hidden at this point, so the
  // measured position must land without the spring gliding in from wherever the
  // unmeasured render put it.
  useLayoutEffect(() => {
    if (!(animatePosition && placement && size)) {
      return;
    }
    if (leftOverride === undefined) {
      if (placedRef.current) {
        springLeft.set(placement.left);
      } else {
        springLeft.jump(placement.left);
      }
    }
    if (topOverride === undefined) {
      if (placedRef.current) {
        springTop.set(placement.top);
      } else {
        springTop.jump(placement.top);
      }
    }
    placedRef.current = true;
  });

  const isFlipped = flippedOverride ?? placement?.flipped ?? false;

  // The flip baseline comes from the first measured render, so a flip decision
  // taken before the panel had a size never replays the entrance.
  useEffect(() => {
    if (!measured) {
      return;
    }
    if (prevFlipRef.current === null) {
      prevFlipRef.current = isFlipped;
      return;
    }
    if (prevFlipRef.current !== isFlipped) {
      setFlipKey((k) => k + 1);
      prevFlipRef.current = isFlipped;
    }
  }, [isFlipped, measured]);

  if (!mounted) {
    return null;
  }

  const portalTarget = isViewport ? document.body : containerEl;
  if (!portalTarget) {
    return null;
  }

  const finalLeft = animatePosition
    ? (leftOverride ?? springLeft)
    : (placement?.left ?? 0);
  const finalTop = animatePosition
    ? (topOverride ?? springTop)
    : (placement?.top ?? 0);

  const slide = entrance && !reduceMotion ? 4 : 0;
  // Without an entrance the panel must already be opaque on its first painted
  // frame — a zero-duration fade still costs one frame at opacity 0.
  const fadeFrom = entrance ? 0 : 1;
  const wrapperHidden = isViewport
    ? { opacity: fadeFrom, y: -slide }
    : { opacity: fadeFrom };
  const wrapperShown = isViewport ? { opacity: 1, y: 0 } : { opacity: 1 };
  const wrapperTransition = isViewport
    ? { duration: entrance ? 0.15 : 0, ease: "easeOut" as const }
    : { duration: entrance ? 0.1 : 0 };

  const panelEntrance =
    entrance && !isViewport
      ? {
          initial: reduceMotion
            ? { scale: 1, opacity: 0, x: 0 }
            : {
                scale: 0.85,
                opacity: 0,
                x: placeAbove ? 0 : isFlipped ? 20 : -20,
              },
          animate: { scale: 1, opacity: 1, x: 0 },
          transition: reduceMotion
            ? { duration: 0 }
            : { type: "spring" as const, stiffness: 300, damping: 25 },
        }
      : null;

  return createPortal(
    <AnimatePresence>
      {hasPanel && (
        <motion.div
          animate={measured ? wrapperShown : wrapperHidden}
          className={cn("pointer-events-none", className)}
          exit={wrapperHidden}
          initial={wrapperHidden}
          ref={wrapperRef}
          style={{
            position: isViewport ? "fixed" : "absolute",
            left: finalLeft,
            top: finalTop,
            width: "max-content",
            maxWidth: isViewport
              ? "calc(100vw - 16px)"
              : frameWidth > offset * 2
                ? frameWidth - offset * 2
                : undefined,
            visibility: measured ? "visible" : "hidden",
            ...style,
          }}
          transition={wrapperTransition}
        >
          <motion.div
            animate={
              panelEntrance
                ? measured
                  ? panelEntrance.animate
                  : panelEntrance.initial
                : undefined
            }
            className={panelClassName}
            initial={panelEntrance?.initial}
            // The slide direction is baked into `initial` at mount, so the panel
            // is remounted once at reveal — before that the flip is unknowable
            // without guessing a size.
            key={panelEntrance ? `${flipKey}-${measured}` : flipKey}
            style={{
              ...(isViewport
                ? null
                : {
                    transformOrigin: placeAbove
                      ? placement?.below
                        ? "center top"
                        : "center bottom"
                      : isFlipped
                        ? "right top"
                        : "left top",
                  }),
              ...panelStyle,
            }}
            transition={panelEntrance?.transition}
          >
            {swapKey === undefined ? (
              body
            ) : (
              <AnimatedTabPanel
                activeKey={swapKey}
                blur={5}
                className="relative"
                duration={0.22}
                layoutDuration={0.28}
                layoutMode={measured ? "size" : false}
                mode="popLayout"
                y={4}
              >
                {body}
              </AnimatedTabPanel>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    portalTarget
  );
}

FloatingTooltip.displayName = "FloatingTooltip";
